import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      // Fail silently — mostrare stesso messaggio previene enumerazione email
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app grid md:grid-cols-2">
      <div className="hidden md:flex items-end p-12 relative bg-gradient-to-br from-rose-warm/30 to-brand/20">
        <div className="relative z-10">
          <BrandLogo size={48} />
          <h1 className="font-display text-4xl font-medium text-primary-fg mt-6">Family Brain</h1>
          <p className="text-secondary-fg mt-2 max-w-md">Recupera l&apos;accesso alla memoria della tua famiglia.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {!sent && (
            <Link to="/auth" data-testid="forgot-password-back-link" className="inline-flex items-center gap-2 text-sm text-tertiary-fg hover:text-primary-fg mb-8">
              <ArrowLeft className="w-4 h-4" /> Torna al login
            </Link>
          )}

          {!sent ? (
            <>
              <h2 className="font-display text-3xl font-medium mb-2">Password dimenticata</h2>
              <p className="text-secondary-fg mb-8">
                Inserisci la tua email. Se corrisponde a un account, ti invieremo un link per reimpostare la password.
              </p>
              <form onSubmit={submit} data-testid="forgot-password-form" className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary-fg" />
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@esempio.it"
                    disabled={loading}
                    className="input-warm w-full pl-10"
                    data-testid="forgot-password-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  data-testid="forgot-password-submit"
                  className="w-full bg-brand text-white rounded-full py-3 font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? "Invio..." : "Invia link di reset"}
                </button>
              </form>
              <p className="text-xs text-tertiary-fg mt-6 text-center">
                Il link sarà valido per 1 ora e potrà essere usato una sola volta.
              </p>
            </>
          ) : (
            <div data-testid="forgot-password-sent" className="text-center">
              <div className="w-16 h-16 rounded-full bg-sage/20 mx-auto flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-sage" />
              </div>
              <h2 className="font-display text-3xl font-medium mb-3">Controlla la tua email</h2>
              <p className="text-secondary-fg mb-6">
                Se l&apos;indirizzo <strong className="text-primary-fg">{email}</strong> è registrato su Family Brain,
                riceverai a breve un&apos;email con il link per reimpostare la password.
              </p>
              <p className="text-sm text-tertiary-fg mb-8">
                Non vedi l&apos;email? Controlla la cartella spam o riprova dopo qualche minuto.
              </p>
              <Link
                to="/auth"
                data-testid="forgot-password-return-login"
                className="inline-block bg-elevated border border-subtle rounded-full py-2.5 px-6 text-sm hover:bg-subtle"
              >
                Torna al login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
